import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../api/auth';

const ParentGrades = () => {
  const { childId } = useParams();
  const [child, setChild] = useState(null);
  const [grades, setGrades] = useState([]);
  const [subjectAverages, setSubjectAverages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubject, setSelectedSubject] = useState('all');

  useEffect(() => {
    if (childId) {
      fetchGrades(childId);
    } else {
      fetchFirstChild();
    }
  }, [childId]);

  const fetchFirstChild = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/parent/children', {
        headers: { Authorization: `Bearer ${token}` }
      });
      let childrenList = response.data;
      if (response.data && response.data.data) childrenList = response.data.data;
      if (Array.isArray(childrenList) && childrenList.length > 0) {
        fetchGrades(childrenList[0].id);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Erreur:', error);
      setLoading(false);
    }
  };

  const fetchGrades = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/parent/children/${id}/grades`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChild(response.data.child);
      setGrades(response.data.grades || []);
      setSubjectAverages(response.data.subject_averages || []);
    } catch (error) {
      console.error('Erreur:', error);
      setGrades([]);
      setSubjectAverages([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredGrades = selectedSubject === 'all'
    ? grades
    : grades.filter(g => g.subject === selectedSubject);

  const getGradeColor = (score) => {
    if (score >= 16) return 'text-green-600 font-bold';
    if (score >= 12) return 'text-blue-600';
    if (score >= 10) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getAverageColor = (avg) => {
    if (avg >= 16) return 'text-green-600';
    if (avg >= 12) return 'text-blue-600';
    if (avg >= 10) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#2C3E50]">📊 Notes et résultats</h1>
        <p className="text-gray-500">
          {child?.name || 'Élève'} - {child?.class_name || ''}
        </p>
      </div>

      {/* Moyennes par matière */}
      {subjectAverages.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {subjectAverages.map((item, index) => (
            <div key={index} className="bg-white rounded-xl shadow-md p-4 text-center">
              <p className="text-sm text-gray-500">{item.subject}</p>
              <p className={`text-2xl font-bold ${getAverageColor(item.average)}`}>
                {item.average}/20
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Filtre par matière */}
      <div className="bg-white rounded-xl shadow-md p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Filtrer par matière
        </label>
        <select
          value={selectedSubject}
          onChange={(e) => setSelectedSubject(e.target.value)}
          className="w-full md:w-1/3 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
        >
          <option value="all">Toutes les matières</option>
          {subjectAverages.map((s, i) => (
            <option key={i} value={s.subject}>{s.subject}</option>
          ))}
        </select>
      </div>

      {/* Tableau des notes */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Matière</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Évaluation</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Note</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Coeff.</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Date</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Appréciation</th>
              </tr>
            </thead>
            <tbody>
              {filteredGrades.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-8 text-gray-500">
                    Aucune note enregistrée
                  </td>
                </tr>
              ) : (
                filteredGrades.map((grade) => (
                  <tr key={grade.id} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">{grade.subject}</td>
                    <td className="py-3 px-4">{grade.evaluation}</td>
                    <td className={`py-3 px-4 font-bold ${getGradeColor(parseFloat(grade.score))}`}>
                      {parseFloat(grade.score).toFixed(2)}/{grade.max_score}
                    </td>
                    <td className="py-3 px-4">{grade.coefficient}</td>
                    <td className="py-3 px-4 text-sm">{grade.date}</td>
                    <td className="py-3 px-4 text-sm text-gray-500">{grade.appreciation || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ParentGrades;